'use client';

/**
 * Project Specification Form Content
 * Main form component with all interactive features and real-time validation
 */

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { submitProjectSpecification } from '@/services/projectSpecificationService';
import type {
  ProjectSpecification,
  FloorMeasurement,
  DrawingData,
} from '@/types/project-specification.types';
import DrawingCanvas from './DrawingCanvas';

interface FormErrors {
  [key: string]: string;
}

interface FieldValidation {
  [key: string]: {
    isValid: boolean;
    message: string;
    touched: boolean;
  };
}

interface ToastNotification {
  id: string;
  type: 'success' | 'error';
  title: string;
  message: string;
  submissionId?: string;
  emailSent?: boolean;
  timestamp: Date;
}

export default function ProjectSpecificationFormContent() {
  // Form state
  const [formData, setFormData] = useState<Partial<ProjectSpecification>>({
    customer_name: '',
    company_name: '',
    contact_email: '',
    contact_phone: '',
    project_address: '',
    number_of_stops: 2,
    elevator_type: 'Hydro',
    total_travel_distance: 0,
    overhead_measurement: 0,
    pit_measurement: 0,
    rh_interlocks: 0,
    lh_interlocks: 0,
    cab_finish: '',
    gate_type: '',
    fixtures_specification: '',
    lighting_specification: '',
    special_requirements: '',
  });

  const [floorMeasurements, setFloorMeasurements] = useState<FloorMeasurement[]>([
    { from_floor: 0, to_floor: 1, distance_inches: 0 },
    { from_floor: 1, to_floor: 2, distance_inches: 0 },
  ]);

  const [drawingData, setDrawingData] = useState<DrawingData | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Replace success/error states with toast notification system
  const [toastNotifications, setToastNotifications] = useState<ToastNotification[]>([]);

  // Real-time field validation state
  const [fieldValidation, setFieldValidation] = useState<FieldValidation>({});
  const [showValidationSummary, setShowValidationSummary] = useState(false);

  // Add ref to track if we're currently submitting
  const isSubmittingRef = useRef(false);

  /**
   * Add a toast notification
   */
  const addToastNotification = (notification: Omit<ToastNotification, 'id' | 'timestamp'>) => {
    const newToast: ToastNotification = {
      ...notification,
      id: `toast-${Date.now()}-${Math.random()}`,
      timestamp: new Date(),
    };
    
    console.log('🎨 Adding toast notification:', newToast.type, newToast.title);
    setToastNotifications((prev) => [...prev, newToast]);
  };

  /**
   * Remove a toast notification
   */
  const removeToastNotification = (id: string) => {
    console.log('🗑️ Removing toast notification:', id);
    setToastNotifications((prev) => prev.filter((toast) => toast.id !== id));
  };

  // Update floor measurements when number of stops changes
  // FIX: Only run this effect when NOT submitting to prevent data clearing
  useEffect(() => {
    // Skip this effect if we're currently submitting the form
    if (isSubmittingRef.current) {
      console.log('⏸️ Skipping floor measurements update - form is submitting');
      return;
    }

    const numStops = formData.number_of_stops || 2;
    console.log(`📏 Floor measurements effect triggered - ${numStops} stops`);
    
    const newMeasurements: FloorMeasurement[] = [];

    // Always start from Ground (0) to Floor 1
    const existingGroundToOne = floorMeasurements.find(
      (m) => m.from_floor === 0 && m.to_floor === 1
    );
    newMeasurements.push(
      existingGroundToOne || {
        from_floor: 0,
        to_floor: 1,
        distance_inches: 0,
      }
    );

    // Add remaining floor-to-floor measurements
    for (let i = 1; i < numStops; i++) {
      const existingMeasurement = floorMeasurements.find(
        (m) => m.from_floor === i && m.to_floor === i + 1
      );
      newMeasurements.push(
        existingMeasurement || {
          from_floor: i,
          to_floor: i + 1,
          distance_inches: 0,
        }
      );
    }

    // Only update if the structure actually changed
    const hasChanges = 
      newMeasurements.length !== floorMeasurements.length ||
      newMeasurements.some((newM, idx) => {
        const oldM = floorMeasurements[idx];
        return !oldM || 
          newM.from_floor !== oldM.from_floor || 
          newM.to_floor !== oldM.to_floor;
      });

    if (hasChanges) {
      console.log('✏️ Updating floor measurements structure');
      setFloorMeasurements(newMeasurements);
    } else {
      console.log('✅ Floor measurements structure unchanged');
    }
  }, [formData.number_of_stops]); // Remove floorMeasurements from dependency array

  /**
   * Real-time field validation logic
   */
  const validateField = (name: string, value: any): { isValid: boolean; message: string } => {
    switch (name) {
      case 'customer_name':
        if (!value || value.trim().length === 0) {
          return { isValid: false, message: 'Customer name is required' };
        }
        if (value.trim().length < 2) {
          return { isValid: false, message: 'Customer name must be at least 2 characters' };
        }
        return { isValid: true, message: 'Valid customer name' };

      case 'contact_email':
        if (!value || value.trim().length === 0) {
          return { isValid: false, message: 'Email address is required' };
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return { isValid: false, message: 'Please enter a valid email address (e.g., email@example.com)' };
        }
        return { isValid: true, message: 'Valid email address' };

      case 'contact_phone':
        if (value && value.trim().length > 0) {
          const phoneRegex = /^[\d\s\-\(\)\+]+$/;
          if (!phoneRegex.test(value)) {
            return { isValid: false, message: 'Phone number can only contain numbers, spaces, and - ( ) +' };
          }
          const digitsOnly = value.replace(/\D/g, '');
          if (digitsOnly.length < 10) {
            return { isValid: false, message: 'Phone number must have at least 10 digits' };
          }
        }
        return { isValid: true, message: 'Valid phone number' };

      case 'project_address':
        if (!value || value.trim().length === 0) {
          return { isValid: false, message: 'Project address is required' };
        }
        if (value.trim().length < 10) {
          return { isValid: false, message: 'Please provide a complete address (at least 10 characters)' };
        }
        return { isValid: true, message: 'Valid project address' };

      case 'overhead_measurement':
        const overhead = parseFloat(value);
        if (isNaN(overhead) || overhead <= 0) {
          return { isValid: false, message: 'Overhead measurement must be greater than 0 inches' };
        }
        if (overhead < 48) {
          return { isValid: false, message: 'Overhead measurement seems unusually low (minimum recommended: 48")' };
        }
        if (overhead > 240) {
          return { isValid: false, message: 'Overhead measurement seems unusually high (maximum typical: 240")' };
        }
        return { isValid: true, message: `Valid overhead: ${overhead.toFixed(2)}"` };

      case 'pit_measurement':
        const pit = parseFloat(value);
        if (isNaN(pit) || pit <= 0) {
          return { isValid: false, message: 'Pit measurement must be greater than 0 inches' };
        }
        if (pit < 6) {
          return { isValid: false, message: 'Pit measurement seems unusually low (minimum recommended: 6")' };
        }
        if (pit > 72) {
          return { isValid: false, message: 'Pit measurement seems unusually high (maximum typical: 72")' };
        }
        return { isValid: true, message: `Valid pit depth: ${pit.toFixed(2)}"` };

      case 'total_travel_distance':
        const travel = parseFloat(value);
        if (isNaN(travel) || travel <= 0) {
          return { isValid: false, message: 'Total travel distance must be greater than 0 inches' };
        }
        if (travel < 96) {
          return { isValid: false, message: 'Total travel distance seems too short (minimum typical: 96" / 8 feet)' };
        }
        if (travel > 720) {
          return { isValid: false, message: 'Total travel distance seems too long (maximum typical: 720" / 60 feet)' };
        }
        return { isValid: true, message: `Valid total travel: ${travel.toFixed(2)}" (${(travel / 12).toFixed(2)} feet)` };

      default:
        return { isValid: true, message: '' };
    }
  };

  /**
   * Validate floor measurements in real-time
   */
  const validateFloorMeasurements = (): { isValid: boolean; message: string } => {
    const hasInvalidMeasurements = floorMeasurements.some(
      (m) => !m.distance_inches || m.distance_inches <= 0
    );

    if (hasInvalidMeasurements) {
      return {
        isValid: false,
        message: 'All floor-to-floor distances must be greater than 0 inches',
      };
    }

    const floorTotal = floorMeasurements.reduce(
      (sum, measurement) => sum + (measurement.distance_inches || 0),
      0
    );
    const totalTravel = formData.total_travel_distance || 0;
    const difference = Math.abs(floorTotal - totalTravel);

    if (totalTravel === 0) {
      return {
        isValid: false,
        message: 'Please enter Total Travel Distance first',
      };
    }

    if (difference > 0.1) {
      return {
        isValid: false,
        message: `Floor measurements total (${floorTotal.toFixed(2)}") must equal Total Travel (${totalTravel.toFixed(2)}"). Difference: ${difference.toFixed(2)}"`,
      };
    }

    return {
      isValid: true,
      message: `✓ Floor measurements total matches Total Travel: ${floorTotal.toFixed(2)}"`,
    };
  };

  /**
   * Handle field blur for real-time validation
   */
  const handleFieldBlur = (fieldName: string, value: any) => {
    const validation = validateField(fieldName, value);
    setFieldValidation((prev) => ({
      ...prev,
      [fieldName]: {
        ...validation,
        touched: true,
      },
    }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const inputElement = e.target as HTMLInputElement;

    const newValue =
      type === 'number'
        ? parseFloat(value) || 0
        : type === 'checkbox'
        ? inputElement.checked
        : value;

    console.log(`📝 Form field changed: ${name} = ${newValue}`);

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: newValue,
      };
      console.log('📋 Updated form data:', updated);
      return updated;
    });

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Clear field validation on change (will re-validate on blur)
    if (fieldValidation[name]?.touched) {
      setFieldValidation((prev) => ({
        ...prev,
        [name]: {
          ...prev[name],
          touched: false,
        },
      }));
    }
  };

  const handleFloorMeasurementChange = (index: number, value: string) => {
    console.log(`📐 Floor measurement changed: index ${index} = ${value}`);
    
    const newMeasurements = [...floorMeasurements];
    newMeasurements[index].distance_inches = parseFloat(value) || 0;
    
    console.log('📏 Updated floor measurements:', newMeasurements);
    setFloorMeasurements(newMeasurements);

    // Clear floor measurement errors
    if (errors.floor_measurements || errors.floor_measurements_total) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.floor_measurements;
        delete newErrors.floor_measurements_total;
        return newErrors;
      });
    }
  };

  /**
   * Comprehensive pre-submission validation
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const newFieldValidation: FieldValidation = {};

    // Validate all required fields
    const fieldsToValidate = [
      'customer_name',
      'contact_email',
      'project_address',
      'overhead_measurement',
      'pit_measurement',
      'total_travel_distance',
    ];

    fieldsToValidate.forEach((fieldName) => {
      const value = formData[fieldName as keyof typeof formData];
      const validation = validateField(fieldName, value);
      newFieldValidation[fieldName] = { ...validation, touched: true };

      if (!validation.isValid) {
        newErrors[fieldName] = validation.message;
      }
    });

    // Validate optional phone if provided
    if (formData.contact_phone && formData.contact_phone.trim().length > 0) {
      const phoneValidation = validateField('contact_phone', formData.contact_phone);
      newFieldValidation.contact_phone = { ...phoneValidation, touched: true };
      if (!phoneValidation.isValid) {
        newErrors.contact_phone = phoneValidation.message;
      }
    }

    // Validate floor measurements
    const floorValidation = validateFloorMeasurements();
    if (!floorValidation.isValid) {
      newErrors.floor_measurements = floorValidation.message;
    }

    setFieldValidation(newFieldValidation);
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('🚀 FORM SUBMIT INITIATED');
    console.log('📊 Current form data at submit:', formData);
    console.log('📏 Current floor measurements at submit:', floorMeasurements);
    console.log('🖼️ Current drawing data at submit:', drawingData);
    
    // Set submitting flag to prevent floor measurements effect from running
    isSubmittingRef.current = true;
    console.log('🔒 Submission lock activated - preventing state updates');
    
    setShowValidationSummary(true);

    if (!validateForm()) {
      console.log('❌ Form validation failed');
      isSubmittingRef.current = false; // Release lock on validation failure
      
      // Scroll to first error
      const firstErrorElement = document.querySelector('.border-red-500');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    console.log('✅ Form validation passed - proceeding with submission');
    setIsSubmitting(true);

    try {
      console.log('🚀 Starting form submission...');
      
      // Create specification object with current form data
      const specification: ProjectSpecification = {
        ...formData,
        drawing_data: drawingData || undefined,
      } as ProjectSpecification;

      console.log('📤 Calling submitProjectSpecification with:', {
        specification,
        floor_measurements: floorMeasurements,
      });

      const { data, error } = await submitProjectSpecification({
        specification,
        floor_measurements: floorMeasurements,
      });

      console.log('📥 Response received - Data:', data, 'Error:', error);

      if (error) {
        console.error('❌ Submission error:', error);
        
        // Show error toast notification
        addToastNotification({
          type: 'error',
          title: 'Submission Failed',
          message: error.message || 'Failed to submit project specification. Please try again.',
        });
        
        setIsSubmitting(false);
        isSubmittingRef.current = false; // Release lock on error
        return;
      }

      if (!data) {
        console.error('❌ No data returned from submission');
        
        addToastNotification({
          type: 'error',
          title: 'Submission Failed',
          message: 'Submission failed - no response received. Please try again.',
        });
        
        setIsSubmitting(false);
        isSubmittingRef.current = false; // Release lock on error
        return;
      }

      console.log('✅ Submission successful! ID:', data.id);
      
      // Show success toast notification with submission details
      addToastNotification({
        type: 'success',
        title: 'Specification Submitted Successfully!',
        message: `Your project specification has been submitted. Our team will review your details and contact you at ${formData.contact_email} within 24 hours.`,
        submissionId: data.id,
        emailSent: true,
      });
      
      setShowValidationSummary(false);
      setIsSubmitting(false);
      isSubmittingRef.current = false; // Release lock after success

      console.log('🎉 Form submission complete - data preserved for user review');
      // DO NOT automatically reset form - let user decide when to clear
    } catch (error) {
      console.error('❌ Unexpected submission error:', error);
      
      addToastNotification({
        type: 'error',
        title: 'Unexpected Error',
        message: 'An unexpected error occurred. Please try again.',
      });
      
      setIsSubmitting(false);
      isSubmittingRef.current = false; // Release lock on unexpected error
    }
  };

  const resetForm = () => {
    console.log('🔄 Manual form reset initiated');
    
    setFormData({
      customer_name: '',
      company_name: '',
      contact_email: '',
      contact_phone: '',
      project_address: '',
      number_of_stops: 2,
      elevator_type: 'Hydro',
      total_travel_distance: 0,
      overhead_measurement: 0,
      pit_measurement: 0,
      rh_interlocks: 0,
      lh_interlocks: 0,
      cab_finish: '',
      gate_type: '',
      fixtures_specification: '',
      lighting_specification: '',
      special_requirements: '',
    });
    setFloorMeasurements([
      { from_floor: 0, to_floor: 1, distance_inches: 0 },
      { from_floor: 1, to_floor: 2, distance_inches: 0 },
    ]);
    setDrawingData(null);
    setErrors({});
    setFieldValidation({});
    setShowValidationSummary(false);
    setIsSubmitting(false);
    isSubmittingRef.current = false;
    setToastNotifications([]);
    
    console.log('✅ Form reset complete');
  };

  /**
   * Render validation icon
   */
  const renderValidationIcon = (fieldName: string) => {
    const validation = fieldValidation[fieldName];
    if (!validation?.touched) return null;

    return (
      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
        {validation.isValid ? (
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ) : (
          <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        )}
      </div>
    );
  };

  /**
   * Render validation message
   */
  const renderValidationMessage = (fieldName: string) => {
    const validation = fieldValidation[fieldName];
    if (!validation?.touched) return null;

    return (
      <p
        className={`text-sm mt-1 ${
          validation.isValid ? 'text-green-600' : 'text-red-600'
        }`}
      >
        {validation.message}
      </p>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast Notification Container - Fixed Position */}
      <div className="fixed top-20 right-4 z-[60] space-y-3 max-w-md">
        {toastNotifications.map((toast) => (
          <div
            key={toast.id}
            className={`${
              toast.type === 'success' ?'bg-green-50 border-green-200' :'bg-red-50 border-red-200'
            } border rounded-lg shadow-lg p-6 animate-slide-in-right`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start flex-1">
                {toast.type === 'success' ? (
                  <svg
                    className="w-6 h-6 text-green-600 mr-3 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-6 h-6 text-red-600 mr-3 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
                <div className="flex-1">
                  <h3
                    className={`text-lg font-semibold ${
                      toast.type === 'success' ? 'text-green-900' : 'text-red-900'
                    }`}
                  >
                    {toast.title}
                  </h3>
                  <p
                    className={`text-sm mt-1 ${
                      toast.type === 'success' ? 'text-green-700' : 'text-red-700'
                    }`}
                  >
                    {toast.message}
                  </p>
                  
                  {/* Submission Details */}
                  {toast.submissionId && (
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <div className="space-y-2">
                        <div className="flex items-center text-sm">
                          <svg
                            className="w-4 h-4 text-green-600 mr-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          <span className="font-medium text-green-800">
                            Submission ID:
                          </span>
                          <code className="ml-2 px-2 py-1 bg-green-100 text-green-900 rounded text-xs font-mono">
                            {toast.submissionId.substring(0, 8)}...
                          </code>
                        </div>
                        
                        {toast.emailSent && (
                          <div className="flex items-center text-sm">
                            <svg
                              className="w-4 h-4 text-green-600 mr-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M6 19h14a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v10a2 2 0 002 2z"
                              />
                            </svg>
                            <span className="text-green-700">
                              ✓ Confirmation email sent to{' '}
                              <span className="font-medium">{formData.contact_email}</span>
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Close Button */}
              <button
                onClick={() => removeToastNotification(toast.id)}
                className={`ml-4 flex-shrink-0 ${
                  toast.type === 'success' ?'text-green-400 hover:text-green-600' :'text-red-400 hover:text-red-600'
                } transition-colors`}
                aria-label="Close notification"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Header with Logo */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link href="/homepage" className="inline-block hover:opacity-80 transition-opacity">
            <AppImage
              src="/assets/images/tmpp6j64rno-1763965371090.jpg"
              alt="CR Elevator logo - Click to return to homepage"
              width={200}
              height={67}
              className="h-12 w-auto"
              priority
            />
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Project Specification Form
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Submit detailed elevator project requirements including measurements, equipment
              specifications, and shaft drawings
            </p>
          </div>

          {/* Validation Summary (shown when user tries to submit with errors) */}
          {showValidationSummary && Object.keys(errors).length > 0 && (
            <div className="mb-8 bg-amber-50 border border-amber-200 rounded-lg p-6">
              <div className="flex items-start">
                <svg
                  className="w-6 h-6 text-amber-600 mr-3 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-amber-900 mb-2">
                    Please Fix the Following Issues
                  </h3>
                  <ul className="list-disc list-inside space-y-1 text-amber-800">
                    {Object.entries(errors).map(([field, message]) => (
                      <li key={field} className="text-sm">
                        {message}
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-amber-700 mt-3">
                    Scroll through the form to see detailed error messages next to each field.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8">
            {/* Customer Information Section */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-gray-200">
                Customer Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Customer Name *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleInputChange}
                      onBlur={(e) => handleFieldBlur('customer_name', e.target.value)}
                      className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors ${
                        fieldValidation.customer_name?.touched
                          ? fieldValidation.customer_name.isValid
                            ? 'border-green-500' :'border-red-500' :'border-gray-300'
                      }`}
                      placeholder="Enter customer name"
                    />
                    {renderValidationIcon('customer_name')}
                  </div>
                  {renderValidationMessage('customer_name')}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Company Name
                  </label>
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Enter company name (optional)"
                  />
                  <p className="text-xs text-gray-500 mt-1">Optional</p>
                </div>

                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Contact Email *
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      name="contact_email"
                      value={formData.contact_email}
                      onChange={handleInputChange}
                      onBlur={(e) => handleFieldBlur('contact_email', e.target.value)}
                      className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors ${
                        fieldValidation.contact_email?.touched
                          ? fieldValidation.contact_email.isValid
                            ? 'border-green-500' :'border-red-500' :'border-gray-300'
                      }`}
                      placeholder="email@example.com"
                    />
                    {renderValidationIcon('contact_email')}
                  </div>
                  {renderValidationMessage('contact_email')}
                </div>

                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Contact Phone
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleInputChange}
                      onBlur={(e) => handleFieldBlur('contact_phone', e.target.value)}
                      className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors ${
                        fieldValidation.contact_phone?.touched
                          ? fieldValidation.contact_phone.isValid
                            ? 'border-green-500' :'border-red-500' :'border-gray-300'
                      }`}
                      placeholder="(555) 123-4567"
                    />
                    {renderValidationIcon('contact_phone')}
                  </div>
                  {renderValidationMessage('contact_phone')}
                </div>

                <div className="md:col-span-2 relative">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Project Address *
                  </label>
                  <div className="relative">
                    <textarea
                      name="project_address"
                      value={formData.project_address}
                      onChange={handleInputChange}
                      onBlur={(e) => handleFieldBlur('project_address', e.target.value)}
                      rows={3}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors ${
                        fieldValidation.project_address?.touched
                          ? fieldValidation.project_address.isValid
                            ? 'border-green-500' :'border-red-500' :'border-gray-300'
                      }`}
                      placeholder="Enter complete project address (street, city, state, ZIP)"
                    />
                  </div>
                  {renderValidationMessage('project_address')}
                </div>
              </div>
            </div>

            {/* Project Parameters Section */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-gray-200">
                Project Parameters
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Number of Stops *
                  </label>
                  <select
                    name="number_of_stops"
                    value={formData.number_of_stops}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    {[2, 3, 4, 5, 6].map((num) => (
                      <option key={num} value={num}>
                        {num} Stops
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Elevator Type *
                  </label>
                  <select
                    name="elevator_type"
                    value={formData.elevator_type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    <option value="Hydro">Hydro</option>
                    <option value="Geared">Geared</option>
                  </select>
                </div>

                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Total Travel (inches) *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="total_travel_distance"
                      step="0.01"
                      value={formData.total_travel_distance || ''}
                      onChange={handleInputChange}
                      onBlur={(e) =>
                        handleFieldBlur('total_travel_distance', e.target.value)
                      }
                      className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors ${
                        fieldValidation.total_travel_distance?.touched
                          ? fieldValidation.total_travel_distance.isValid
                            ? 'border-green-500' :'border-red-500' :'border-gray-300'
                      }`}
                      placeholder="264.00"
                    />
                    {renderValidationIcon('total_travel_distance')}
                  </div>
                  {renderValidationMessage('total_travel_distance')}
                  <p className="text-xs text-gray-500 mt-1">
                    Total inches from bottom to top floor
                  </p>
                </div>
              </div>
            </div>

            {/* Measurements Section */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-gray-200">
                Measurements (inches)
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Overhead (OH) *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="overhead_measurement"
                      step="0.01"
                      value={formData.overhead_measurement || ''}
                      onChange={handleInputChange}
                      onBlur={(e) => handleFieldBlur('overhead_measurement', e.target.value)}
                      className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors ${
                        fieldValidation.overhead_measurement?.touched
                          ? fieldValidation.overhead_measurement.isValid
                            ? 'border-green-500' :'border-red-500' :'border-gray-300'
                      }`}
                      placeholder="118.00"
                    />
                    {renderValidationIcon('overhead_measurement')}
                  </div>
                  {renderValidationMessage('overhead_measurement')}
                </div>

                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Pit *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="pit_measurement"
                      step="0.01"
                      value={formData.pit_measurement || ''}
                      onChange={handleInputChange}
                      onBlur={(e) => handleFieldBlur('pit_measurement', e.target.value)}
                      className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-colors ${
                        fieldValidation.pit_measurement?.touched
                          ? fieldValidation.pit_measurement.isValid
                            ? 'border-green-500' :'border-red-500' :'border-gray-300'
                      }`}
                      placeholder="12.50"
                    />
                    {renderValidationIcon('pit_measurement')}
                  </div>
                  {renderValidationMessage('pit_measurement')}
                </div>
              </div>

              {/* Floor-to-Floor Measurements */}
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Floor-to-Floor Distances *
                </h3>
                {errors.floor_measurements && (
                  <p className="text-red-600 text-sm mb-3">{errors.floor_measurements}</p>
                )}
                <div className="space-y-3">
                  {floorMeasurements.map((measurement, index) => {
                    const fromLabel =
                      measurement.from_floor === 0
                        ? 'Ground'
                        : `Floor ${measurement.from_floor}`;
                    const toLabel = `Floor ${measurement.to_floor}`;

                    return (
                      <div key={index} className="flex items-center gap-4">
                        <div className="flex-shrink-0 w-40">
                          <span className="text-sm font-medium text-gray-700">
                            {fromLabel} to {toLabel}:
                          </span>
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          value={measurement.distance_inches || ''}
                          onChange={(e) =>
                            handleFloorMeasurementChange(index, e.target.value)
                          }
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                          placeholder="132.00"
                        />
                        <span className="text-sm text-gray-500 w-16">inches</span>
                      </div>
                    );
                  })}
                </div>

                {/* Real-time Floor Measurement Validation */}
                <div
                  className={`mt-4 p-4 border rounded-lg ${
                    validateFloorMeasurements().isValid
                      ? 'bg-green-50 border-green-200' :'bg-amber-50 border-amber-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-900">
                      Floor Measurements Total:
                    </span>
                    <span className="text-lg font-bold text-gray-900">
                      {floorMeasurements
                        .reduce((sum, m) => sum + (m.distance_inches || 0), 0)
                        .toFixed(2)}
                      "
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-gray-900">
                      Total Travel Target:
                    </span>
                    <span className="text-lg font-bold text-gray-900">
                      {(formData.total_travel_distance || 0).toFixed(2)}
                    </span>
                  </div>
                  <p
                    className={`text-sm font-medium ${
                      validateFloorMeasurements().isValid
                        ? 'text-green-700' :'text-amber-700'
                    }`}
                  >
                    {validateFloorMeasurements().message}
                  </p>
                </div>
              </div>
            </div>

            {/* Interlock Specifications */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-gray-200">
                Interlock Specifications
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    RH Interlocks
                  </label>
                  <select
                    name="rh_interlocks"
                    value={formData.rh_interlocks}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    {[0, 1, 2, 3, 4, 5, 6].map((num) => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    LH Interlocks
                  </label>
                  <select
                    name="lh_interlocks"
                    value={formData.lh_interlocks}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    {[0, 1, 2, 3, 4, 5, 6].map((num) => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Equipment Specifications */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-gray-200">
                Equipment Specifications
              </h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Cab</label>
                  <input
                    type="text"
                    name="cab_finish"
                    value={formData.cab_finish}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder='e.g., 610" White Shaker'
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Gate</label>
                  <input
                    type="text"
                    name="gate_type"
                    value={formData.gate_type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="e.g., Black S/O Gate"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Fixtures
                  </label>
                  <textarea
                    name="fixtures_specification"
                    value={formData.fixtures_specification}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="e.g., Black Fixtures - COP, handrail, car here light on call buttons"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Lights</label>
                  <input
                    type="text"
                    name="lighting_specification"
                    value={formData.lighting_specification}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder='e.g., Cab Lights 4" (3)'
                  />
                </div>
              </div>
            </div>

            {/* Drawing Section */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-gray-200">
                Shaft Diagram & Dimensions
              </h2>
              <p className="text-sm text-gray-600 mb-4">
                Draw the shaft layout and add dimension annotations using your finger or mouse.
              </p>
              <DrawingCanvas onDrawingChange={setDrawingData} />
            </div>

            {/* Special Requirements */}
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-gray-200">
                Special Requirements
              </h2>
              <textarea
                name="special_requirements"
                value={formData.special_requirements}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Any additional notes, special requirements, or considerations..."
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-4">
              <button
                type="button"
                onClick={resetForm}
                className="px-8 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                disabled={isSubmitting}
              >
                Reset Form
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-8 py-3 bg-red-600 text-white rounded-lg font-semibold transition-colors ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-700'
                }`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Specification'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}